import React, { useState, useCallback } from 'react';
import { projectBrandValue, FEATURE_PREDICTIVE } from '../services/predictiveService';
import { useTranslations } from '../hooks/useTranslations';

/**
 * PredictiveModule - Módulo de IA Predictiva para proyección a 5 años
 * 
 * Componente independiente que calcula proyecciones de valor de marca
 * Null-safe y sin efectos secundarios
 */
const PredictiveModule = React.memo(function PredictiveModule({ 
  result, 
  formData 
}) {
  // Get translations and language context
  const { t, language } = useTranslations();
  
  const [isCalculating, setIsCalculating] = useState(false);
  const [projection, setProjection] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  
  // Feature flag check
  if (!FEATURE_PREDICTIVE) {
    return null;
  }
  
  // Safe data extraction with defaults
  const currentValue = result?.totalValue ?? 0;
  const hasValidResult = currentValue > 0;
  
  // Handler para calcular proyección
  const handleCalculateProjection = useCallback(async () => {
    if (isCalculating) return;
    
    setIsCalculating(true);
    setError(null);
    
    try {
      const projectionData = await projectBrandValue({
        currentValue,
        formData: formData ?? {},
        breakdown: result?.breakdown ?? {}
      });
      
      setProjection(projectionData);
      setIsExpanded(true);
      
    } catch (err) {
      console.error('Error calculating projection:', err);
      setError(t('results.predictive5y.error.calculation'));
    } finally {
      setIsCalculating(false);
    }
  }, [currentValue, formData, result, isCalculating]);
  
  // Handler para formatear números con locale
  const formatCurrency = useCallback((value) => {
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }, [language]);

  // Handler para formatear porcentajes con locale
  const formatPercentage = useCallback((value) => {
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      maximumFractionDigits: 1
    }).format(value / 100);
  }, [language]);

  // Función para formatear porcentajes con signo
  const formatPercentSigned = useCallback((n) => {
    const abs = Math.abs(n);
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    const pct = new Intl.NumberFormat(locale, { 
      style: 'percent', 
      minimumFractionDigits: 1, 
      maximumFractionDigits: 1 
    }).format(abs / 100);
    const sign = n > 0 ? '+' : (n < 0 ? '−' : '');
    return `${sign}${pct}`;
  }, [language]);

  // Helper para traducir Analysis Notes del backend (Mejorado)
  const translateAnalysisNotes = useCallback((notes) => {
    if (!notes || !Array.isArray(notes)) return [];

    return notes.map((note, index) => {
      // Convertir nota a minúsculas para comparación y limpiar
      const noteLower = note.toLowerCase().trim();
      
      // Patrón 1: Análisis basado en datos históricos / Historical data analysis
      if (noteLower.includes('análisis basado en') || noteLower.includes('datos históricos') || 
          noteLower.includes('historical data') || noteLower.includes('empresas del sector') ||
          noteLower.includes('companies in the sector') || noteLower.includes('analysis based on')) {
        const companyCount = extractNumber(note) || 50;
        return t('results.predictive5y.notes.bullet1', { companyCount });
      }
      
      // Patrón 2: Proyección considera / Projection considers market trends
      if (noteLower.includes('proyección considera') || noteLower.includes('projection considers') ||
          noteLower.includes('tendencia del mercado') || noteLower.includes('market trend') ||
          noteLower.includes('tendencia increasing') || noteLower.includes('potencial de crecimiento') ||
          noteLower.includes('crecimiento en el sector')) {
        return t('results.predictive5y.notes.bullet3');
      }
      
      // Patrón 3: Volatilidad calibrada / Calibrated volatility  
      if (noteLower.includes('volatilidad calibrada') || noteLower.includes('calibrated volatility') ||
          noteLower.includes('datos históricos reales') || noteLower.includes('historical data') ||
          noteLower.includes('enfoque conservador') || noteLower.includes('conservative approach')) {
        const volatility = extractPercentage(note) || '20.4%';
        return t('results.predictive5y.notes.bullet5', { volatility });
      }
      
      // Patrón 4: Madurez empresarial / Business maturity
      if (noteLower.includes('madurez empresarial') || noteLower.includes('business maturity') || 
          noteLower.includes('proyección ajusta') || noteLower.includes('adjusts growth') ||
          noteLower.includes('posición emergente') || noteLower.includes('emerging position')) {
        return t('results.predictive5y.notes.bullet2');
      }
      
      // Patrón 5: Factores macroeconómicos / Macroeconomic factors
      if (noteLower.includes('factores macroeconómicos') || noteLower.includes('macroeconomic factors') ||
          noteLower.includes('impacto de factores') || noteLower.includes('previstos para') ||
          noteLower.includes('2025–2030') || noteLower.includes('2025-2030')) {
        return t('results.predictive5y.notes.bullet4');
      }
      
      // Patrón 6: Validación de mercado / Market validation
      if (noteLower.includes('validación de mercado') || noteLower.includes('market validation') ||
          noteLower.includes('valoración inicial') || noteLower.includes('percentil') ||
          noteLower.includes('percentil 75') || noteLower.includes('ingresos')) {
        const level = extractValidationLevel(note);
        const levelLabel = t(`results.predictive5y.notes.levels.${level}`);
        return t('results.predictive5y.notes.bullet6', { levelLabel });
      }
      
      // Patrón 7: Alineación con mercado / Market alignment
      if (noteLower.includes('alineación con') || noteLower.includes('market alignment') ||
          noteLower.includes('posicionamiento inicial') || noteLower.includes('presencia social') ||
          noteLower.includes('bajo posicionamiento') || noteLower.includes('crecimiento proyectado más alto')) {
        const alignment = extractAlignment(note);
        const alignmentLabel = t(`results.predictive5y.notes.alignment.${alignment}`);
        return t('results.predictive5y.notes.bullet7', { alignmentLabel });
      }
      
      // Patrón 8: Condiciones del sector / Sector conditions
      if (noteLower.includes('condiciones del sector') || noteLower.includes('sector conditions') ||
          noteLower.includes('crecimiento proyectado') || noteLower.includes('projected growth') ||
          noteLower.includes('crecimiento anual del') || noteLower.includes('annual growth')) {
        const condition = extractCondition(note);
        const conditionLabel = t(`results.predictive5y.notes.conditions.${condition}`);
        return t('results.predictive5y.notes.bullet8', { conditionLabel });
      }
      
      // NUEVOS PATRONES ESPECÍFICOS basados en feedback del testing
      
      // Patrón 9: Análisis genérico - catch remaining Spanish analysis text
      if (language === 'en' && (
          noteLower.includes('análisis') || noteLower.includes('proyección') || 
          noteLower.includes('valoración') || noteLower.includes('considerando') ||
          noteLower.includes('relativa al') || noteLower.includes('inicialmente por') ||
          noteLower.includes('del sector'))) {
        
        // Determinar qué bullet usar basado en contenido
        if (noteLower.includes('datos') || noteLower.includes('empresas')) {
          const companyCount = extractNumber(note) || 50;
          return t('results.predictive5y.notes.bullet1', { companyCount });
        }
        if (noteLower.includes('volatilidad') || noteLower.includes('calibrada')) {
          const volatility = extractPercentage(note) || '20.4%';
          return t('results.predictive5y.notes.bullet5', { volatility });
        }
        if (noteLower.includes('tendencia') || noteLower.includes('mercado')) {
          return t('results.predictive5y.notes.bullet3');
        }
        if (noteLower.includes('valoración') || noteLower.includes('percentil')) {
          const level = extractValidationLevel(note);
          const levelLabel = t(`results.predictive5y.notes.levels.${level}`);
          return t('results.predictive5y.notes.bullet6', { levelLabel });
        }
        if (noteLower.includes('crecimiento') || noteLower.includes('posicionamiento')) {
          const alignment = extractAlignment(note);
          const alignmentLabel = t(`results.predictive5y.notes.alignment.${alignment}`);
          return t('results.predictive5y.notes.bullet7', { alignmentLabel });
        }
        
        // Fallback para análisis no específico
        return t('results.predictive5y.notes.bullet2');
      }
      
      // Si nada coincide, devolver la nota original
      return note;
    });
  }, [t, language]);

  // Helpers mejorados para extraer valores de las notas del backend
  const extractNumber = (text) => {
    // Buscar números de empresas, incluyendo formatos con comas
    const match = text.match(/(\d{1,3}(?:,\d{3})*|\d+)/);
    return match ? parseInt(match[1].replace(/,/g, '')) : null;
  };

  const extractPercentage = (text) => {
    // Buscar porcentajes con formato ±XX.X% o XX.X%
    const match = text.match(/±?(\d+\.?\d*)%/);
    return match ? `${match[1]}%` : null;
  };

  const extractValidationLevel = (text) => {
    const textLower = text.toLowerCase();
    if (textLower.includes('alta') || textLower.includes('high') || textLower.includes('percentil 75') || textLower.includes('percentile 75')) return 'high';
    if (textLower.includes('baja') || textLower.includes('low') || textLower.includes('percentil 25') || textLower.includes('percentile 25')) return 'low';
    return 'medium';
  };

  const extractAlignment = (text) => {
    const textLower = text.toLowerCase();
    if (textLower.includes('conservador') || textLower.includes('conservative') || textLower.includes('bajo posicionamiento')) return 'conservative';
    if (textLower.includes('optimista') || textLower.includes('optimistic') || textLower.includes('alto posicionamiento')) return 'optimistic';
    return 'moderate';
  };

  const extractCondition = (text) => {
    const textLower = text.toLowerCase();
    if (textLower.includes('inestable') || textLower.includes('unstable') || textLower.includes('declining')) return 'unstable';
    if (textLower.includes('estable') || textLower.includes('steady') || textLower.includes('stable')) return 'steady';
    return 'neutral';
  };
  
  // Handler para obtener color según crecimiento
  const getGrowthColor = useCallback((growth) => {
    if (growth >= 20) return 'text-green-400';
    if (growth >= 10) return 'text-blue-400';
    if (growth >= 5) return 'text-yellow-400';
    return 'text-gray-400';
  }, []);
  
  // Handler para obtener icono de confianza
  const getConfidenceIcon = useCallback((confidence) => {
    switch (confidence) {
      case 'high': return '🎯';
      case 'medium': return '📊';
      case 'low': return '⚠️';
      default: return '📈';
    }
  }, []);
  
  // Handler para exportar PDF
  const handleExportPDF = useCallback(async () => {
    if (isExportingPDF || !projection) return;
    
    setIsExportingPDF(true);
    setError(null); // Limpiar errores anteriores
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      if (!backendUrl) {
        throw new Error('Backend URL not configured');
      }
      
      const exportData = {
        current_valuation: currentValue,
        projection_data: {
          yearly: projection.yearly || [],
          assumptions: projection.assumptions || {},
          notes: projection.notes || ''
        },
        form_data: formData ?? {},
        include_market_validation: true,
        include_historical_data: true,
        language: language // Send current language to backend
      };
      
      console.log('🔍 Iniciando descarga PDF...', exportData);
      
      const response = await fetch(`${backendUrl}/api/export-prediction-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(exportData)
      });
      
      console.log('📥 Respuesta recibida:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      
      // Verificar que la respuesta es realmente un PDF
      const contentType = response.headers.get('Content-Type');
      console.log('📄 Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('application/pdf')) {
        throw new Error('La respuesta no es un archivo PDF válido');
      }
      
      // Get filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Reporte_Proyeccion_${formData?.companyName || 'Marca'}_${new Date().toISOString().slice(0,10)}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      console.log('💾 Nombre archivo:', filename);
      
      // Download PDF
      const blob = await response.blob();
      console.log('📦 Blob creado:', blob.size, 'bytes, tipo:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('El archivo PDF está vacío');
      }
      
      // Crear URL y descargar
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      a.target = '_blank'; // Ayuda con algunos navegadores
      
      // Agregar al DOM, hacer click, y limpiar
      document.body.appendChild(a);
      console.log('🖱️ Ejecutando click de descarga...');
      a.click();
      
      // Limpiar después de un breve delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
        console.log('✅ Descarga completada y recursos limpiados');
      }, 100);
      
      // Método alternativo si la descarga automática falla
      setTimeout(() => {
        if (document.body.contains(a)) {
          console.log('⚠️ Descarga automática falló, intentando método alternativo...');
          // Abrir en nueva ventana como fallback
          window.open(url, '_blank');
        }
      }, 2000);
      
    } catch (err) {
      console.error('❌ Error exporting PDF:', err);
      setError(`Error al exportar PDF: ${err.message}`);
    } finally {
      setIsExportingPDF(false);
    }
  }, [currentValue, projection, formData, isExportingPDF]);
  
  // Componente para estado sin resultado
  if (!hasValidResult) {
    return (
      <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-2xl p-6 border border-indigo-500/30">
        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
          <span className="text-2xl mr-2">🤖</span>
          {t('results.predictive5y.title')}
        </h3>
        
        <div className="bg-slate-700/30 p-4 rounded-lg text-center">
          <div className="text-4xl mb-3">📊</div>
          <h4 className="font-semibold text-gray-300 mb-2">
            {t('results.predictive5y.noResult.title')}
          </h4>
          <p className="text-sm text-gray-400">
            {t('results.predictive5y.noResult.description')}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-2xl p-6 border border-indigo-500/30">
      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
        <span className="text-2xl mr-2">🤖</span>
        {t('results.predictive5y.title')}
      </h3>
      
      <div className="bg-slate-700/30 p-4 rounded-lg mb-4">
        <p className="text-gray-300 text-sm mb-4">
          {t('results.predictive5y.subtitle')}
        </p>
        
        {!projection && !isCalculating && (
          <div className="text-center">
            <button
              onClick={handleCalculateProjection}
              disabled={isCalculating}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto
                sm:px-6 sm:py-3
                max-sm:px-4 max-sm:py-2.5 max-sm:text-sm max-sm:rounded-lg
                max-sm:[&>span]:mr-1 max-sm:[&>span]:text-base
                [&>span]:inline-block [&>span]:whitespace-nowrap
                max-sm:whitespace-nowrap max-sm:flex-nowrap"
            >
              <span className="whitespace-nowrap flex items-center">🔮</span>
              <span className="whitespace-nowrap">{t('results.predictive5y.actions.calculate')}</span>
            </button>
          </div>
        )}
        
        {isCalculating && (
          <div className="text-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-3"></div>
            <p className="text-white font-semibold">{t('results.predictive5y.actions.processing')}</p>
            <p className="text-gray-400 text-sm">{t('results.predictive5y.actions.analyzing')}</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4">
            <p className="text-red-300 text-sm">{error}</p>
            <button
              onClick={handleCalculateProjection}
              className="text-red-400 hover:text-red-300 text-sm underline mt-2"
            >
              {t('results.predictive5y.actions.retry')}
            </button>
          </div>
        )}
      </div>
      
      {projection && isExpanded && (
        <div className="space-y-4">
          {/* Tabla de proyección */}
          <div className="bg-slate-700/30 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-slate-600/50 border-b border-slate-500">
              <h4 className="font-semibold text-white flex items-center gap-2">
                {getConfidenceIcon(projection.assumptions?.confidence)}
                {t('results.predictive5y.yearly.title')}
              </h4>
            </div>
            
            <div className="divide-y divide-slate-600">
              {projection.yearly?.map((yearData, index) => (
                <div key={`year-${yearData.year}`} className="px-4 py-3 flex justify-between items-center hover:bg-slate-600/20 transition-colors">
                  <div>
                    <span className="text-white font-semibold">
                      {t('results.predictive5y.yearly.year', { num: index + 1, year: yearData.year })}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold text-lg">
                      {formatCurrency(yearData.value)}
                    </div>
                    <div className={`text-sm ${getGrowthColor(yearData.growth)}`}>
                      {t('results.predictive5y.yearly.growth', { 
                        value: formatPercentSigned(yearData.growth)
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Supuestos y factores */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-700/30 p-4 rounded-lg">
              <h5 className="font-semibold text-indigo-400 mb-3">📈 {t('results.predictive5y.assumptions.title')}</h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('results.predictive5y.assumptions.baseGrowth')}:</span>
                  <span className="text-white font-semibold">
                    {((projection.assumptions?.growth ?? 0) * 100).toFixed(1)}% {t('results.predictive5y.assumptions.annual')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('results.predictive5y.assumptions.volatility')}:</span>
                  <span className="text-white font-semibold">
                    ±{((projection.assumptions?.volatility ?? 0) * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">{t('results.predictive5y.assumptions.confidence')}:</span>
                  <span className="text-white font-semibold capitalize">
                    {projection.assumptions?.confidence ?? 'medium'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-700/30 p-4 rounded-lg">
              <h5 className="font-semibold text-purple-400 mb-3">🎯 {t('results.predictive5y.target.title')}</h5>
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">
                  {formatCurrency(projection.yearly?.[4]?.value ?? 0)}
                </div>
                <div className="text-sm text-gray-300 mb-2">
                  {t('results.predictive5y.target.subtitle', { year: new Date().getFullYear() + 5 })}
                </div>
                <div className="text-sm text-green-400">
                  {t('results.predictive5y.target.totalGrowth', { 
                    value: formatPercentSigned(Math.round(((projection.yearly?.[4]?.value ?? currentValue) / currentValue - 1) * 100))
                  })}
                </div>
              </div>
            </div>
          </div>
          
          {/* Notas explicativas */}
          <div className="bg-slate-700/30 p-4 rounded-lg">
            <h5 className="font-semibold text-white mb-3">💡 {t('results.predictive5y.notes.title')}</h5>
            <div className="space-y-2">
              {translateAnalysisNotes(projection.notes).map((translatedNote, index) => (
                <p key={`note-${index}`} className="text-sm text-gray-300 flex items-start gap-2">
                  <span className="text-indigo-400 mt-1">•</span>
                  {translatedNote}
                </p>
              ))}
            </div>
          </div>
          
          {/* Disclaimer */}
          <div className="bg-slate-800/50 p-3 rounded-lg border border-slate-600">
            <p className="text-xs text-gray-400 text-center">
              🔬 {t('results.predictive5y.disclaimer')}
            </p>
          </div>
          
          {/* Botón para recalcular */}
          <div className="text-center pt-2">
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setProjection(null);
                  setIsExpanded(false);
                }}
                className="text-indigo-400 hover:text-indigo-300 text-sm underline"
              >
                {t('results.predictive5y.actions.recalculate')}
              </button>
              
              <button
                onClick={handleExportPDF}
                disabled={isExportingPDF}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isExportingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {t('results.predictive5y.actions.exporting')}
                  </>
                ) : (
                  <>
                    <span>📄</span>
                    {t('results.predictive5y.actions.exportPdf')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PredictiveModule;